import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check, LifeBuoy, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "technical", label: "Technical problem", icon: LifeBuoy },
  { id: "feedback", label: "Feedback or idea", icon: Lightbulb },
];

export default function Support() {
  const { user } = useAuth();
  const [category, setCategory] = useState("technical");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!email.trim()) {
      toast.error("Please add an email so we can reach you");
      return;
    }
    setLoading(true);
    try {
      await api.post("/support", {
        text: text.trim(),
        name: name.trim(),
        email: email.trim(),
        category,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#07090E]">
      <header className="sticky top-0 z-20 bg-[#07090E]/85 backdrop-blur-md border-b border-[#1E273D]">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="support-logo">
            <img src="/openword-logo.png" alt="" className="w-6 h-6" />
            <span className="text-lg text-white">OpenWord AI</span>
          </Link>
          <Link to="/study" data-testid="support-back" className="text-sm text-slate-500 hover:text-[#00E5FF] transition-colors">
            Back to app
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <h1 className="text-3xl text-white mb-2">How can we help?</h1>
        <p className="text-sm text-slate-400 mb-8 leading-relaxed">
          Report a problem or share an idea. We'll email you an update once it's resolved — no need to
          chase anyone down.
        </p>

        {sent ? (
          <div
            data-testid="support-success"
            className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 text-center space-y-3"
          >
            <div className="w-12 h-12 rounded-full bg-[#00E5FF] flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <p className="text-slate-300 leading-relaxed">
              Thank you — we've received this. We've sent a confirmation to your email and will let you
              know as soon as it's resolved.
            </p>
            <Button
              data-testid="support-submit-another"
              onClick={() => {
                setSent(false);
                setText("");
              }}
              variant="outline"
              className="rounded-full border-[#29344D]"
            >
              Submit another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase tracking-widest text-slate-400">
                What's this about?
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`support-category-${id}`}
                    aria-pressed={category === id}
                    onClick={() => setCategory(id)}
                    className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium border transition-colors ${
                      category === id
                        ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]"
                        : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:border-[#00E5FF]/40"
                    }`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                data-testid="support-name"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]"
              />
              <Input
                data-testid="support-email"
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]"
              />
            </div>

            <Textarea
              data-testid="support-text"
              placeholder={
                category === "technical"
                  ? "What happened? Where in the app did it happen, and what did you expect?"
                  : "Tell us your idea or what would make your study richer."
              }
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              required
              className="rounded-xl border-[#29344D] bg-[#0E131F] resize-none focus-visible:ring-[#00E5FF]/30"
            />

            <Button
              data-testid="support-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              We'll only use your email to follow up on this message.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
